// @flow
import React, { PureComponent } from "react";
import classnames from "classnames";
import get from "lodash/get";
import getLightboxDescription from "../../helpers/api/lightbox";
import Loader from "../Loader";
import imgClose from "../../img/close.svg";
import css from "./Lightbox.css";
import { type Props } from "./";

type State = {|
  description: string,
  isLoading: boolean,
  show: boolean,
  error: string,
|};

// Increase for more pacman!
const FAKE_LATENCY = 1000;

export default class Lightbox extends PureComponent<Props, State> {
  state = {
    description: "",
    isLoading: false,
    show: false,
    error: "",
  };

  // Listeners for using the ESC key to close the lightbox
  componentDidMount = () =>
    document.addEventListener("keydown", this.onKeyDown, false);
  componentWillUnmount = () =>
    document.removeEventListener("keydown", this.onKeyDown, false);

  // Open the lightbox
  onOpen = () => {
    if (!this.state.show) {
      // We're about to toggle and SHOW the lightbox
      this.fetchDescription();
      this.disableScrolling();
    }
    this.setState({ show: true });
  };

  // Close the lightbox
  onClose = () => {
    // We're about to toggle and STOP showing the lightbox
    if (this.state.show) {
      this.cleanup();
      this.setState({ description: "", show: false });
    }
  };

  // Handle hitting the ESC key to close the lightbox
  onKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 27 && this.state.show) this.onClose();
  };

  // We'll set scroll overflow on the body element to prevent scrolling
  // Side-effect to prevent scrolling. Should be stored in a global modal
  // style component for all modals/popups
  getBodyStyle = () =>
    typeof document !== "undefined" ? get(document, "body.style") : undefined;

  // If not using state management like Redux, mobx-state-tree, etc. to cache
  // API data, we can still track the async network call states and result data
  // with React state. If there was more time, it might be better to
  // abstract this into the Container that wraps this Component and use a
  // library like `recompose` to manage the React state.
  fetchDescription = async () => {
    try {
      this.setState({ isLoading: true });
      const description = await getLightboxDescription();
      this.timeout = setTimeout(() => {
        this.setState({
          description,
          isLoading: false,
        });
      }, FAKE_LATENCY);
    } catch (e) {
      this.setState({
        isLoading: false,
        error:
          "Clever error message to entertain your audience and make them forgive you for not fetching your data",
      });
    }
  };

  disableScrolling = () => {
    // Side-effect to prevent scrolling. Should be stored in a global modal
    // style component for all modals/popups
    const style = this.getBodyStyle();
    if (style) style.overflow = "hidden";
  };

  enableScrolling = () => {
    // Side-effect to prevent scrolling. Should be stored in a global modal
    // style component for all modals/popups
    const style = this.getBodyStyle();
    if (style && style.overflow === "hidden") style.overflow = "visible";
  };

  cleanup = () => {
    // Remove timeout if it's still there
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }

    // Re-enable scrolling in case we haven't
    this.enableScrolling();
  };

  timeout: ?TimeoutID;

  render() {
    const { description, error, isLoading, show } = this.state;

    // CSS modules can make these classnames less verbose
    const classLightbox = classnames({
      [css.background]: true,
      [css.show]: show,
    });

    return (
      <React.Fragment>
        <button onClick={this.onOpen} className={css.button}>
          open lightbox
        </button>
        <div className={classLightbox}>
          <button className={css.close} onClick={this.onClose}>
            <img src={imgClose} alt="close" />
          </button>
          <img alt="nature" src="https://placeimg.com/1920/1080/nature" />
          <p>{error || description}</p>
          {isLoading && !description ? <Loader /> : null}
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Lightbox/Lightbox.js